import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react'

const Section = styled.section`
  padding: 96px 24px;
  background: var(--color-bg-elevated);
`

const Inner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  text-align: center;
`

const SectionLabel = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 12px;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 56px;
`

const CarouselContainer = styled.div`
  position: relative;
  max-width: 720px;
  margin: 0 auto;
  min-height: 220px;
`

const QuoteIcon = styled(Quote)`
  color: var(--color-accent);
  opacity: 0.3;
  margin-bottom: 24px;
`

const TestimonialText = styled(motion.blockquote)`
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
  font-style: italic;
  margin-bottom: 24px;
`

const Attribution = styled(motion.div)`
  a {
    font-weight: 600;
    color: var(--color-text);
    font-size: 0.95rem;

    &:hover {
      color: var(--color-accent);
    }
  }

  p {
    font-size: 0.85rem;
    color: var(--color-text-muted);
    margin-top: 4px;
  }
`

const NavButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
`

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg-card-hover);
    color: var(--color-text);
  }
`

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
`

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? 'var(--color-accent)' : 'var(--color-border)')};
  transition: background 0.2s ease;
`

const TESTIMONIALS = [
  {
    text: 'Valyss has been very instrumental through the growth trajectory of Enginologi and a very valuable partner from when we first developed our brand and as we continue to build and execute on our go-to-market strategy.',
    name: 'Markus Hauser',
    title: 'CEO and Founder, Enginologi',
    linkedin: 'https://www.linkedin.com/in/markushauserbiotech/',
  },
  {
    text: "It has been extremely valuable to have Valyss as a partner for our go-to-market strategy and tactics; they started making an immediate impact and have been a fractional CRO & CMO resource for sales and business development, marketing, messaging and value proposition refinement, and even provided investor and prospect introductions.",
    name: 'David Smith',
    title: 'CEO and Founder, MLTwist',
    linkedin: 'https://www.linkedin.com/in/davidsmithdigital/',
  },
  {
    text: "Valyss has been an amazing advisor and partner as we define our brand, value proposition, and go-to-market strategy and tactics. They were even able to make prospective customer and partner introductions that were very important for key market research to refine our product.",
    name: 'Sri Gari',
    title: 'CEO and Founder',
    linkedin: 'https://www.linkedin.com/in/sri-gari-8902263/',
  },
]

export default function TestimonialCarousel() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % TESTIMONIALS.length)
    }, 7000)
    return () => clearInterval(timer)
  }, [])

  const prev = () => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)
  const next = () => setIndex((i) => (i + 1) % TESTIMONIALS.length)

  return (
    <Section>
      <Inner>
        <SectionLabel>Testimonials</SectionLabel>
        <SectionTitle>What our clients are saying</SectionTitle>
        <CarouselContainer>
          <QuoteIcon size={40} />
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
            >
              <TestimonialText>{`"${TESTIMONIALS[index].text}"`}</TestimonialText>
              <Attribution>
                <a href={TESTIMONIALS[index].linkedin} target="_blank" rel="noopener noreferrer">
                  {TESTIMONIALS[index].name}
                </a>
                <p>{TESTIMONIALS[index].title}</p>
              </Attribution>
            </motion.div>
          </AnimatePresence>
          <NavButtons>
            <NavButton onClick={prev} aria-label="Previous testimonial">
              <ChevronLeft size={18} />
            </NavButton>
            <NavButton onClick={next} aria-label="Next testimonial">
              <ChevronRight size={18} />
            </NavButton>
          </NavButtons>
          <Dots>
            {TESTIMONIALS.map((_, i) => (
              <Dot key={i} $active={i === index} onClick={() => setIndex(i)} aria-label={`Go to testimonial ${i + 1}`} />
            ))}
          </Dots>
        </CarouselContainer>
      </Inner>
    </Section>
  )
}
